import { useState, useCallback } from "react";

/**
 * Centralised toast state.
 *
 * Returns:
 *   showToast       – boolean: whether the toast is visible
 *   toastMessage    – string: the message to display
 *   missingFields   – string[]: optional list of missing profile fields
 *   triggerToast    – (message, missingFields?) => void: show the toast
 *   dismissToast    – () => void: hide the toast
 */
const useToast = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [missingFields, setMissingFields] = useState([]);

  const triggerToast = useCallback((message, fields = []) => {
    setToastMessage(message);
    setMissingFields(fields);
    setShowToast(true);
  }, []);

  const dismissToast = useCallback(() => {
    setShowToast(false);
  }, []);

  /**
   * Interprets a Redux action payload and fires the toast if it represents
   * a user-facing error. Handles both string payloads and the
   * { error: "INCOMPLETE_PROFILE", missingFields: [] } object shape.
   */
  const triggerFromPayload = useCallback(
    (payload, fallbackMessage = "An error occurred.") => {
      if (!payload) return;
      if (typeof payload === "object" && payload.error === "INCOMPLETE_PROFILE") {
        triggerToast(
          "Your profile is incomplete. Please complete your profile first.",
          payload.missingFields ?? []
        );
      } else if (payload === "ALREADY_REGISTERED") {
        triggerToast("You are already registered for this sale.");
      } else if (typeof payload === "string") {
        triggerToast(payload);
      } else {
        triggerToast(fallbackMessage);
      }
    },
    [triggerToast]
  );

  return {
    showToast,
    toastMessage,
    missingFields,
    triggerToast,
    dismissToast,
    triggerFromPayload,
  };
};

export default useToast;